import { AddinRoleAdditionMode, ColorDTO, LotInListDTO, ProductForSaleDTO, RemovedIngredientDTO, Res, SizeDTO, SoldLotDTO, SoldProductDTO, SoldProductType } from 'core/api';
import * as AddinComposer from './AddinComposer';
import { getPrice } from './getPrice';
import { getWeight } from './getWeight';
import { Result } from 'core/infrastructure';
import { Message } from 'core/ui-next/modal';

export function createEmptyCompositionStateFor(product: ProductForSaleDTO, quantity: number): CompositionState {

	const composition: CompositionState = {
		selectedProduct: null,
		addinsSelection: {
			composition: AddinComposer.createEmptyAddinComposition(product),
			mode: AddinRoleAdditionMode.UseSameAddinForAll
		},
		size: { Id: 0, Name: '', Priority: 0 },
		color: { Id: 0, Name: '', Priority: 0, Value: '' },
		lotSelection: {
			criteria: '',
			searchedLots: [],
			selected: { Id: 0, Number: '' },
			mode: 'assign',
			showOnlyWithStock: false
		},
		price: 0,
		removedIngredients: [],
		preparationNotes: '',
		quantity
	};

	return composition;
}


function isComposable(product: ProductForSaleDTO) {
	return !product.IsSubFamily && !product.IsSubCategory && !product.HasMoreFormats;
}

export async function createCompositionStateFor(product: ProductForSaleDTO, quantity: number): Promise<Result<CompositionState>> {

	if (!isComposable(product))
		throw new Error('El producto debería ser un producto que se pueda componer (Standard o MenuHeader)');

	if (product.RequiresCouponCode && product.CouponCode === '') {
		await Message.show(Res.Msg.MSG_PRODUCT_REQUIRES_COUPON_CODE());
		return Result.error();
	}

	if (product.CannotBeSold) {
		await Message.show(product.CannotBeSoldReason);
		return Result.error();
	}

	const priceResult = await getPrice(product);
	if (!priceResult.ok)
		return Result.error();

	const quantityResult = product.IsSoldByWeight ? await getWeight({ SaleFormatName: product.SaleFormatName, Price: priceResult.value }) : Result.ok(quantity);
	if (!quantityResult.ok)
		return Result.error();

	return Result.ok({
		selectedProduct: product,
		price: priceResult.value,
		quantity: quantityResult.value,
		addinsSelection: {
			composition: AddinComposer.createEmptyAddinComposition(product),
			mode: AddinRoleAdditionMode.UseSameAddinForAll
		},
		size: { Id: 0, Name: '', Priority: 0 },
		color: { Id: 0, Name: '', Priority: 0, Value: '' },
		lotSelection: {
			criteria: '',
			mode: 'assign',
			searchedLots: [],
			selected: { Id: 0, Number: '' },
			showOnlyWithStock: false
		},
		removedIngredients: [],
		preparationNotes: ''
	});
}


// Parémetros para la composición. Producto que voy a componer con su preparationType, etc.
export type CompositionParams = {
	product: ProductForSaleDTO,
	type: SoldProductType,
	groupName: string,
	preparationOrderId: number,
	preparationTypeId: number
}

// Esto debería ser el resultado final del Wizard, lo que ha conseguido despues de rellenar todo
export type CompositionResult = {
	product: SoldProductDTO,
	addins: SoldProductDTO[]
	quantity: number;
	preparationNotes: string;
	removedIngredients: RemovedIngredientDTO[];
}

// Este tipo es lo que Wizard nos va rellenanado en cada paso. 
export type CompositionState = {
	selectedProduct: Nullable<ProductForSaleDTO>
	quantity: number;
	addinsSelection: {
		composition: AddinComposer.Composition;
		mode: AddinRoleAdditionMode;
	}
	preparationNotes: string;
	size: SizeDTO;
	color: ColorDTO;
	lotSelection: {
		selected: SoldLotDTO;
		mode: 'assign' | 'ignore';
		criteria: string,
		searchedLots: LotInListDTO[],
		showOnlyWithStock: boolean
	},
	price: number;
	removedIngredients: RemovedIngredientDTO[];
};

export type CompositionStateValue =
	| CompositionState
	| ((previous: CompositionState) => CompositionState);

export type SetCompositionState = (value: CompositionStateValue) => void;
